import React, { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { getColor } from '../../styling/mixins/colors';
import { fontFamilyBold, text } from '../../styling/mixins/typography';
import { OrderRow } from '../../Types/OrderRow';
import QuantityInput from '../QuantityInput/QuantityInput';
import Icon from '../Icon';
import { translate } from '../../Services/translation';

interface OrderRowProductCardProps {
  row: OrderRow;
  onRemoveClick?: (row: OrderRow) => void;
  className?: string;
  onQuantityChange: (quantity: number, articleNumber: string, abortController: AbortController) => void;
}

const OrderRowProductCard = ({
  row,
  onRemoveClick,
  className = '',
  onQuantityChange
}: OrderRowProductCardProps) => {
  const dispatch = useDispatch();
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleOnQuantityChange = useCallback(
    (quantity: number) => {
      // // Abort any ongoing request if exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const newAbortController = new AbortController();
      abortControllerRef.current = newAbortController;

      onQuantityChange(quantity, row.articleNumber, abortControllerRef.current)

    },
    [dispatch]
  );

  return (
    <StyledOrderRowProductCard className={`${className ?? ''}`}>
      <div className="image-wrapper">
        {row.image && (
          <a href={row.url}>
            <img className="image" src={row.image} alt={row.alt} />
          </a>
        )}
      </div>
      <div className="right-section">
        <div className="top-section">
          <div className="content">
            <div className="overline">{row.brand}</div>
            <div className="name">
              <a href={row.url}>{row.name}</a>
            </div>
            {row.extraInfo && (
              <div>
                {row.extraInfo.items.map((item) => (
                  <div className="extra-row" key={'mini-cart' + item.key}>
                    {item.key}: {item.value}
                  </div>
                ))}
              </div>
            )}
            <div className="stock-status">
              <span className="stock-status-description">
                {row.stockStatus.description}
              </span>
            </div>
          </div>
          <div>
            {onRemoveClick && !row.isFreeGift && (
              <button className="btn--remove" role="button">
                <Icon
                  role="button"
                  className="close"
                  name="close"
                  onClick={() => onRemoveClick && onRemoveClick(row)}
                />
              </button>
            )}
          </div>
        </div>
        <div className="footer">
          <QuantityInput
            quantity={row.quantity}
            showButtons={!row.isFreeGift}
            disabled={row.isFreeGift}
            wrapperClassName="quantity-input"
            onChange={(quantity) => handleOnQuantityChange(quantity)}
          />
          <div
            className={`price-wrapper ${row.totalCampaignPrice != '' && 'price-wrapper--discount'
              }`}
          >
            {row.totalCampaignPrice != ''
              ? row.totalCampaignPrice
              : row.totalPrice}
            {row.totalCampaignPrice != '' && (
              <div className="price">{row.totalPrice}</div>
            )}
          </div>
        </div>
      </div>
    </StyledOrderRowProductCard>
  );
};

export default OrderRowProductCard;

const StyledOrderRowProductCard = styled.div`
  background-color: ${getColor('white')};
  display: flex;
  flex-direction: row;
  gap: 0 1.6rem;
  padding: 1.6rem;
  margin-top: 0.4rem;
  // border: 1px solid ${getColor('gray', 200)};

  .top-section {
    display: flex;
    flex-direction: row;
  }
  .right-section {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .image-wrapper {
    background-color: ${getColor('white')};
    width: 64px;
    height: 64px;
  }

  .image {
    width: 100%;
    aspect-ratio: 1/1;
    object-fit: contain;
  }

  .content {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .overline {
    ${text('medium')}
    color: ${getColor('grey', 500)};
    margin-bottom: 0.2rem;
  }

  .name {
    ${text('medium')}
    ${fontFamilyBold}
    margin-bottom: 0.8rem;
  }
  .close {
    font-size: 1.5rem;
  }

  .extra-row {
    ${text('medium')}
    color: ${getColor('grey', 500)}
  }

  .btn--remove {
    ${text('medium')}
    color: ${getColor('grey', 500)};
    text-decoration: underline;
    cursor: pointer;
    margin-left: 1.6rem;
  }

  .quantity-input {
    max-width: 112px;

    input {
      padding-top: 0.3rem;
      padding-bottom: 0.3rem;
    }
  }

  .stock-status {
    margin-bottom: 0.8rem;
    svg {
      display: none;
    }
    .stock-status-description {
      ${text('small')};
      color: ${getColor('gray', 600)};
    }
  }

  .price-wrapper {
    ${text('large')}
    ${fontFamilyBold}
    text-align: right;
    &--discount {
      color: ${getColor('alert')};
      .price {
      }
    }
  }

  .price {
    ${text('small')}
    text-decoration: line-through;
    color: ${getColor('grey', 500)};
  }

  .footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`;